import {
  CheckRounded as CheckRoundedIcon,
  ContentCopyRounded as ContentCopyRoundedIcon,
  DeleteRounded as DeleteRoundedIcon,
  MailRounded as MailRoundedIcon
} from '@mui/icons-material'
import {
  Box,
  ButtonGroup,
  Chip,
  IconButton,
  Pagination,
  PaginationItem,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { t } from 'i18next'
import { useState } from 'react'

import { APP_BASE_URL } from '../../../configs/environment.js'
import Can from '../../Layout/Can/Can.jsx'
import { useGlobalContext } from '../../Shared/Contexts/GlobalContext.jsx'
import { useInvites } from '../Contexts/InvitesContext.jsx'

const DEFAULT_PAGE_SIZE = 25
const PendingInvitesList = ({ invites, page, onChangePage, onInviteDeleted }) => {
  const { useDeleteInvite, useResendInvite } = useInvites()
  const { openErrorAlert, openSuccessAlert } = useGlobalContext()
  const [copied, setCopied] = useState(null)

  const handleResend = (email, inviteId) => {
    useResendInvite({
      email,
      inviteId
    })
      .then(() => openSuccessAlert())
      .catch((err) => openErrorAlert({ keys: err.keys }))
  }

  const handleDelete = (inviteId) => {
    useDeleteInvite(inviteId)
      .then(() => {
        onInviteDeleted()
        openSuccessAlert()
      })
      .catch((err) => openErrorAlert({ keys: err.keys }))
  }

  const handleContentCopyRounded = (id) => {
    setCopied(id)
    navigator.clipboard.writeText(`${APP_BASE_URL}/invites/${id}`)
  }

  return (
    <>
      <Stack
        my={4}
        spacing={2}>
        {invites?.data.map((invite) => (
          <Box key={invite?.id}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
              mx={2}
              my={1}>
              <Box sx={{ width: '50%' }}>
                <Typography variant="h5">{invite.email}</Typography>
                <Typography
                  variant="body2"
                  id={`invite-url-${invite.email}`}>
                  <small>{`${APP_BASE_URL}/invites/${invite.id}`}</small>
                </Typography>
              </Box>
              <Box sx={{ width: '30%' }}>
                <Chip
                  color="primary"
                  variant="standard"
                  label={invite.organization?.name}
                />
              </Box>
              <Box sx={{ width: '10%' }}>
                <Chip
                  color="primary"
                  variant="standard"
                  label={invite.role.name}
                />
              </Box>
              <Box sx={{ width: '10%' }}>
                <ButtonGroup size="small">
                  <Can action={'users_d'}>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => handleDelete(invite.id)}
                        id={`delete-invite-${invite.email}`}>
                        <DeleteRoundedIcon
                          color="error"
                          fontSize="small"
                        />
                      </IconButton>
                    </Tooltip>
                  </Can>
                  <Tooltip title="Resend">
                    <IconButton
                      onClick={() => handleResend(invite.email, invite.id)}
                      id={`resend-invite-${invite.email}`}>
                      <MailRoundedIcon
                        color="primary"
                        fontSize="small"
                      />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Copy link">
                    <IconButton
                      onClick={() => handleContentCopyRounded(invite.id)}
                      id={`copy-link-invite-${invite.email}`}>
                      {copied !== invite.id ? (
                        <ContentCopyRoundedIcon
                          color="primary"
                          fontSize="small"
                        />
                      ) : (
                        <CheckRoundedIcon
                          color="primary"
                          fontSize="small"
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                </ButtonGroup>
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>
      <Box
        display="flex"
        justifyContent="center">
        {invites && invites?.count > DEFAULT_PAGE_SIZE && (
          <Pagination
            count={Math.ceil(invites?.count / DEFAULT_PAGE_SIZE)}
            page={page}
            onChange={onChangePage}
            renderItem={(item) => {
              const ariaLabels = {
                first: t('pagination.firstPage'),
                last: t('pagination.lastPage'),
                next: t('pagination.nextPage'),
                previous: t('pagination.previousPage'),
                page: t('pagination.goToPage', { page: item.page })
              }

              return (
                <PaginationItem
                  {...item}
                  aria-label={ariaLabels[item.type]}
                />
              )
            }}
            showFirstButton
            showLastButton
          />
        )}
      </Box>
    </>
  )
}

export default PendingInvitesList
